import {
    drawBlackBorderToRoofEdges,
    drawRoofForGivenFace,
    drawWallForGivenEdge,
    GetHouseBound,
    GetIdStr,
} from "./Loader";
import {m2ft} from "../../../../../Constants";
import {
    AddIndividualMode,
    addModulesToRoofAutoFill,
    DeleteModule,
    SwitchModuleIrrMat,
    updateModuleCount,
} from "./ModuleControl";
import {
    ACCESS_MULTIPLIERS,
    FACE_IDEAL_API,
    MODULE_GRAPH,
    SHADE_CALCULATIONS,
    siteCalculationState,
} from "../../../../../services/siteCalculationState";
import {useViewerStore} from "../../../../../store/store";
import * as THREE from "three";
import {getModuleMeshInfo, GetRadVal, getRoofModuleMeshes,} from "./Common";

import {FaceItem, ModuleData, RoofMesh, Vertex3D,} from "../../../../../@types/types";
import {Viewer} from "../Viewer-Factory/Viewer";
import {disposeObject} from "./MeshControl";

/* -------------------------------------------------------------------------- */
/*                               CREATE GROUND MOUNT                           */
/* -------------------------------------------------------------------------- */

export function CreatePlaneRoof(
    self: Viewer,
    position: { x: number; z: number },
    props: any
): void {
    const dis = props?.scl ?? {x: 6.096, y: 6.096};
    const tilt = props?.oriAng?.tilt ?? 0;
    const azimuth = props?.oriAng?.azimuth ?? 0;

    const {roofs} = self;
    const {edges, faces} = roofs[0];

    const roofFaceId: string = props?.selRoofId ?? GetIdStr();
    const idStr = faces.length.toString();

    const pos3DArr: Vertex3D[] = [];
    const lines: any[] = [];
    const newEdges: any[] = [];

    const rotX = GetRadVal(180 - tilt);
    const rotY = GetRadVal(azimuth);

    const halfWidth = dis.x / 2;
    const halfDepth = dis.y / 2;

    const localCorners = [
        {x: -halfWidth, y: 0, z: -halfDepth},
        {x: -halfWidth, y: 0, z: halfDepth},
        {x: halfWidth, y: 0, z: halfDepth},
        {x: halfWidth, y: 0, z: -halfDepth},
    ];

    const cosX = Math.cos(rotX);
    const sinX = Math.sin(rotX);

    const tiltedCorners = localCorners.map(corner => ({
        x: corner.x,
        y: corner.y * cosX - corner.z * sinX,
        z: corner.y * sinX + corner.z * cosX,
    }));

    const cosY = Math.cos(rotY);
    const sinY = Math.sin(rotY);

    const rotatedCorners = tiltedCorners.map(corner => ({
        x: corner.x * cosY - corner.z * sinY,
        y: corner.y,
        z: corner.x * sinY + corner.z * cosY,
    }));

    rotatedCorners.forEach(corner => {
        const y = corner.y + (props?.height ?? 3.048);
        pos3DArr.push({
            x: corner.x + position.x,
            y,
            z: corner.z + position.z,
            oldY: y,
            zeroY: corner.y,
        });
    });

    const getBuildingNumber = (): number =>
        faces.length
            ? Math.max(...faces.map((f: FaceItem) => f.buildingName)) + 1
            : 1;

    const getRoofTagId = (): number =>
        self.roofMeshArr.length
            ? Math.max(
            ...self.roofMeshArr.map(
                (rm: RoofMesh) => rm.userData.roof_tag_id
            )
        ) + 1
            : 1;

    const buildingName = props?.buildingName ?? getBuildingNumber();

    const newFace: FaceItem & any = {
        roofId: idStr,
        roofFaceId,
        surfaceMount: true,
        posArr: pos3DArr,
        pitch: tilt,
        azimuth,
        irrDir: {tilt: -tilt, azimuth: -azimuth},
        lines,
        size: dis.x * dis.y * m2ft * m2ft,
        oriAng: {azimuth, tilt},
        flatRoof: true,
        plane: true,
        scl: {x: dis.x, y: dis.y},
        pos: {x: position.x, y: position.z},
        height: props?.height ?? 3.048,
        carport: props?.carport ?? false,
        buildingName,
        roof_tag_id: getRoofTagId(),
        moduleArr: props?.moduleArr ?? [],
    };

    faces.push(newFace);

    pos3DArr.forEach((pos3D, idx) => {
        const nextPos = pos3DArr[(idx + 1) % pos3DArr.length];
        const points = [pos3D, nextPos];
        const lineId = GetIdStr();

        const edge = {
            posArr: points,
            type: "rake",
            flat: true,
            roofFaceId,
            carport: newFace.carport,
            roof_tag_id: newFace.roof_tag_id,
        };

        edges.push(edge);
        newEdges.push(edge);

        lines.push({
            points,
            lineId,
            pass: true,
            possiblePathway: false,
            type: "RAKE",
            flatRoof: true,
        });
    });

    const roofGroup = new THREE.Group() as any;
    roofGroup.houseId = GetIdStr();

    //@ts-ignore
    drawRoofForGivenFace({
        face: newFace,
        associatedRoofGroup: roofGroup,
        isDrawingGroundMount: true,
        meshArr: self.meshArr,
        roofMeshArr: self.roofMeshArr,
    });

    newEdges.forEach(edge => {
        drawWallForGivenEdge({
            edge,
            associatedRoofGroup: roofGroup,
            meshArr: self.meshArr,
            houseId: roofGroup.houseId,
        });
    });

    const boundMesh = GetHouseBound(roofGroup);

    roofGroup.scale.y = roofGroup.sclY;
    roofGroup.rotation.y = self.roofRotY || 0;
    roofGroup.userData.originalPosition = roofGroup.position;

    self.roofGroup.add(roofGroup);
    self.boundGroup.add(boundMesh);

    self.roofMeshArr.forEach((roofMesh: RoofMesh) => {
        drawBlackBorderToRoofEdges({
            self,
            associatedRoofGroup: roofGroup,
            roofMesh,
        });
    });

    const roofMesh: RoofMesh | undefined = self.roofMeshArr.find(
        (m: RoofMesh) => m.roofFaceId === roofFaceId
    );
    if (!roofMesh) return;

    roofMesh.moduleArr = newFace.moduleArr;

    if (newFace.moduleArr.length) {
        newFace.moduleArr.forEach((item: ModuleData) => {
            AddIndividualMode(
                roofMesh,
                item,
                true,
                self.moduleMeshArr,
                self.meshArr,
                false,
                true
            );
        });

        useViewerStore.setState({
            //@ts-ignore
            savedRoofModulesLayout: self.roofMeshArr.map((roof: RoofMesh) => ({
                roofFaceId: roof.roofFaceId,
                moduleArr: getRoofModuleMeshes(roof).map(getModuleMeshInfo),
            })),
        });
    } else {
        //@ts-ignore
        addModulesToRoofAutoFill(self, roofMesh, null, "land", true);
    }

    siteCalculationState.needsUpdate(ACCESS_MULTIPLIERS);
    siteCalculationState.useCalculations(ACCESS_MULTIPLIERS);
    siteCalculationState.needsUpdate(FACE_IDEAL_API);
    siteCalculationState.useCalculations(FACE_IDEAL_API);

    self.refreshShade();
    SwitchModuleIrrMat(self, true);
    self.setCreatePlaneMode(false);
    self.ifContainsFlatRoofAndEnableBallastedRacking();
    //@ts-ignore
    updateModuleCount(self, "land", false, roofFaceId);

    setTimeout(() => SelectFlatRoof(self, roofFaceId), 100);
}

/* -------------------------------------------------------------------------- */
/*                               UPDATE GROUND MOUNT                          */
/* -------------------------------------------------------------------------- */

export function UpdatePlaneRoof(
    self: Viewer,
    property: string,
    value: any,
    isUndo: boolean
): void {
    const {selFlatRoof} = self;
    if (!selFlatRoof) return;

    const selRoofId = selFlatRoof.roofFaceId;
    const faceIndex = self.roofs[0].faces.findIndex(
        (f: FaceItem) => f.roofFaceId === selRoofId
    );
    if (faceIndex === -1) return;

    const roofInfo = self.roofs[0].faces[faceIndex];
    const oldModuleLayout = JSON.parse(JSON.stringify(selFlatRoof.moduleArr ?? []));

    DeletePlaneRoof(self);

    const oldValue =
        property === "carport" ? roofInfo.carport :
            property === "height" ? roofInfo.height :
                property === "azimuth" ? roofInfo.oriAng.azimuth :
                    property === "pitch" ? roofInfo.oriAng.tilt :
                        property === "pos" ? roofInfo.pos :
                            property === "sclX" ? roofInfo.scl.x :
                                property === "sclY" ? roofInfo.scl.y : undefined;

    if (property === "pos") {
        roofInfo.pos = {x: value.x, y: value.y};
    } else if (property === "sclX") {
        roofInfo.scl = {...roofInfo.scl, x: value};
    } else if (property === "sclY") {
        roofInfo.scl = {...roofInfo.scl, y: value};
    } else if (property === "pitch") {
        roofInfo.pitch = value;
        roofInfo.oriAng.tilt = value;
        roofInfo.irrDir.tilt = value;
    } else if (property === "azimuth") {
        roofInfo.azimuth = value;
        roofInfo.oriAng.azimuth = value;
        roofInfo.irrDir.azimuth = value;
    } else {
        (roofInfo as any)[property] = value;
    }

    roofInfo.moduleArr = oldModuleLayout;

    CreatePlaneRoof(
        self,
        {x: roofInfo.pos.x, z: roofInfo.pos.y},
        {...roofInfo, selRoofId}
    );

    if (!isUndo) {
        self.addStep("updatePlaneRoof", {property, selRoofId}, oldValue, value);
    }
}

/* -------------------------------------------------------------------------- */
/*                               DELETE GROUND MOUNT                             */
/* -------------------------------------------------------------------------- */

export function DeletePlaneRoof(self: Viewer): void {
    const {selFlatRoof} = self;
    if (!selFlatRoof) return;

    const selRoofId = selFlatRoof.roofFaceId;
    const roofMesh: RoofMesh | undefined = self.roofMeshArr.find(
        (m: RoofMesh) => m.roofFaceId === selRoofId
    );

    if (!roofMesh) return;

    const modules = getRoofModuleMeshes(roofMesh);
    DeleteModule(modules, roofMesh, self.moduleMeshArr, self);

    self.roofs[0].faces = self.roofs[0].faces.filter(
        (f: FaceItem) => f.roofFaceId !== selRoofId
    );
    self.roofs[0].edges = self.roofs[0].edges.filter(
        (e: any) => e.roofFaceId !== selRoofId
    );

    const edgeMeshes = self.edgeMeshArr.filter(
        (m: any) => m.userData.roofFaceId === selRoofId
    );
    const wallMeshes = self.wallMeshArr.filter(
        (m: any) => m.roofFaceId === selRoofId
    );

    disposeObject(roofMesh);
    edgeMeshes.forEach(disposeObject);
    wallMeshes.forEach(disposeObject);

    self.roofMeshArr = self.roofMeshArr.filter((m: RoofMesh) => m !== roofMesh);
    self.edgeMeshArr = self.edgeMeshArr.filter((m: any) => !edgeMeshes.includes(m));
    self.wallMeshArr = self.wallMeshArr.filter((m: any) => !wallMeshes.includes(m));

    siteCalculationState.needsUpdate(MODULE_GRAPH);
    siteCalculationState.needsUpdate(SHADE_CALCULATIONS);
    SwitchModuleIrrMat(self, true);
}

/* -------------------------------------------------------------------------- */
/*                               SELECT GROUND MOUNT                             */
/* -------------------------------------------------------------------------- */

export function SelectFlatRoof(self: Viewer, selRoofId: string): void {
    self.selFlatRoof = self.roofMeshArr.find(
        (r: RoofMesh) => r.roofFaceId === selRoofId
    ) || null;

    const roofInfo = self.roofs[0].faces.find(
        (f: FaceItem) => f.roofFaceId === selRoofId
    );

    if (!self.selFlatRoof || !roofInfo) return;

    self.selFlatRoof.material.color.setHex(0x0000ff);
    self.selFlatRoof.material.map = undefined;

    const {scl, pos, height, oriAng} = roofInfo;

    useViewerStore.setState({
        selectedFlatRoofHeight: height * m2ft,
        selectedFlatRoofAzimuth: oriAng.azimuth,
        selectedFlatRoofPitch: oriAng.tilt,
        selectedFlatRoofPosX: pos.x,
        selectedFlatRoofPosY: pos.y,
        selectedFlatRoofScaleX: scl.x * m2ft,
        selectedFlatRoofScaleY: scl.y * m2ft,
        selectedFlatRoofIsCarport: roofInfo.carport,
    });
}

/* -------------------------------------------------------------------------- */
/*                               GROUND MOUNT CHECK                            */
/* -------------------------------------------------------------------------- */

export const isGroundMount = (roofMesh: RoofMesh): boolean => {
    const sceneViewer = useViewerStore.getState().sceneViewer;
    return Boolean(
        sceneViewer?.roofs[0]?.faces.find(
            (f: FaceItem) => f.roofFaceId === roofMesh.roofFaceId
        )?.surfaceMount
    );
};

/* -------------------------------------------------------------------------- */
/*                               SETUP INIT GROUND MOUNT                            */
/* -------------------------------------------------------------------------- */

export function SetupFlatRoof(
    self: Viewer,
    interObj?: {
        object?: {
            roofFaceId?: string;
        };
    }
): void {
    const flatRoofArr: RoofMesh[] = self.roofMeshArr.filter(
        (item: RoofMesh) => item.flatRoof === true
    );

    // Reset appearance of all flat roofs
    flatRoofArr.forEach((item: RoofMesh) => {
        if (item.material) {
            item.material.color.setHex(item.oriColor);
            item.material.map = item.oriMap;
        }
    });

    self.selFlatRoof = null;

    if (!interObj?.object?.roofFaceId) return;

    SelectFlatRoof(self, interObj.object.roofFaceId);
}

/* -------------------------------------------------------------------------- */
/*                              GROUND MOUNT INIT POINTS                           */
/* -------------------------------------------------------------------------- */

export function initPlaneRoofPoints(self: Viewer): void {
    const posSize = 0.01;

    const virFlatIn = new THREE.Group();
    const virPlaneIn = new THREE.Group();

    const posGeo = new THREE.BoxGeometry(posSize, posSize, posSize);
    const posMat = new THREE.MeshStandardMaterial({color: 0x333333});
    const posMesh = new THREE.Mesh(posGeo, posMat);

    const positions: { x: number; z: number }[] = [
        {x: 1, z: 1},
        {x: 1, z: -1},
        {x: -1, z: -1},
        {x: -1, z: 1},
    ];

    positions.forEach(pos => {
        const virFlatPos = posMesh.clone();
        const virPlanePos = posMesh.clone();

        virFlatPos.position.set(pos.x * 10, 0, pos.z * 10);
        virPlanePos.position.set(pos.x / 2, 0, pos.z / 2);

        virFlatIn.add(virFlatPos);
        virPlaneIn.add(virPlanePos);
    });

    self.virFlatPoints = new THREE.Group();
    self.virFlatPoints.add(virFlatIn);

    self.virPlanePoints = new THREE.Group();
    self.virPlanePoints.add(virPlaneIn);

    self.totalGroup.add(self.virFlatPoints, self.virPlanePoints);

    self.virFlatPlane = new THREE.Plane();
}

